import React, { useState, useEffect } from "react";

const NodeEditPanel = ({
  node,
  onClose,
  onUpdate,
  onDelete,
  edges = [],
  nodes = [],
  onEdgeUpdate,
}) => {
  // Initialize form data with all possible node properties
  const [formData, setFormData] = useState(() => ({
    label: node.data.label || "",
    content: node.data.content || "",
    triggerType: node.data.triggerType || "newMessage",
    conditions: node.data.conditions || [],
    options: node.data.options || "",
    caption: node.data.caption || "",
    delayValue: node.data.delayValue || 0,
    delayUnit: node.data.delayUnit || "minutes",
    webhookUrl: node.data.webhookUrl || "",
    webhookMethod: node.data.webhookMethod || "POST",
    webhookHeaders: node.data.webhookHeaders || [],
    quickReplies: node.data.quickReplies || [],
    webhookParams: node.data.webhookParams || [],
    payloadType: node.data.payloadType || "static",
    payloadTemplate: node.data.payloadTemplate || "{}",
  }));

  const [currentWebhookParam, setCurrentWebhookParam] = useState({
    key: "",
    value: "",
  });

  const [isEditingEdge, setIsEditingEdge] = useState(false);
  const [currentEdge, setCurrentEdge] = useState(null);
  const [currentWebhookHeader, setCurrentWebhookHeader] = useState({
    key: "",
    value: "",
  });
  const [currentQuickReply, setCurrentQuickReply] = useState("");

  useEffect(() => {
    setFormData({
      label: node.data.label || "",
      content: node.data.content || "",
      triggerType: node.data.triggerType || "newMessage",
      conditions: node.data.conditions || [],
      options: node.data.options || "",
      caption: node.data.caption || "",
      delayValue: node.data.delayValue || 0,
      delayUnit: node.data.delayUnit || "minutes",
      webhookUrl: node.data.webhookUrl || "",
      webhookMethod: node.data.webhookMethod || "POST",
      webhookHeaders: node.data.webhookHeaders || [],
      quickReplies: node.data.quickReplies || [],
      webhookParams: node.data.webhookParams || [],
      payloadType: node.data.payloadType || "static",
      payloadTemplate: node.data.payloadTemplate || "{}",
    });
  }, [node]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Common handlers
  const handleEditEdge = (edge) => {
    setCurrentEdge(edge);
    setIsEditingEdge(true);
  };

  const handleSaveEdgeConditions = (conditions) => {
    onEdgeUpdate(currentEdge.id, { ...currentEdge, conditions });
    setIsEditingEdge(false);
  };

  const handleConditionChange = (id, key, value) => {
    setFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c) =>
        c.id === id ? { ...c, [key]: value } : c
      ),
    }));
  };

  // Add these handler functions
  const addWebhookParam = () => {
    if (currentWebhookParam.key.trim() && currentWebhookParam.value.trim()) {
      setFormData((prev) => ({
        ...prev,
        webhookParams: [
          ...prev.webhookParams,
          {
            id: Date.now(),
            key: currentWebhookParam.key,
            value: currentWebhookParam.value,
          },
        ],
      }));
      setCurrentWebhookParam({ key: "", value: "" });
    }
  };

  const removeWebhookParam = (id) => {
    setFormData((prev) => ({
      ...prev,
      webhookParams: prev.webhookParams.filter((p) => p.id !== id),
    }));
  };

  const addCondition = (type = "AND") => {
    setFormData((prev) => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        {
          id: Date.now(),
          field: "incomingMessage",
          operator: "is",
          value: "",
          type,
        },
      ],
    }));
  };

  const removeCondition = (id) => {
    setFormData((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((c) => c.id !== id),
    }));
  };

  // Webhook header handlers
  const addWebhookHeader = () => {
    if (currentWebhookHeader.key.trim() && currentWebhookHeader.value.trim()) {
      setFormData((prev) => ({
        ...prev,
        webhookHeaders: [
          ...prev.webhookHeaders,
          {
            id: Date.now(),
            key: currentWebhookHeader.key,
            value: currentWebhookHeader.value,
          },
        ],
      }));
      setCurrentWebhookHeader({ key: "", value: "" });
    }
  };

  const removeWebhookHeader = (id) => {
    setFormData((prev) => ({
      ...prev,
      webhookHeaders: prev.webhookHeaders.filter((h) => h.id !== id),
    }));
  };

  // Quick reply handlers
  const addQuickReply = () => {
    if (currentQuickReply.trim()) {
      setFormData((prev) => ({
        ...prev,
        quickReplies: [...prev.quickReplies, currentQuickReply.trim()],
      }));
      setCurrentQuickReply("");
    }
  };

  const removeQuickReply = (index) => {
    setFormData((prev) => ({
      ...prev,
      quickReplies: prev.quickReplies.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (node.data.onChange) {
      node.data.onChange(formData);
    }
    onUpdate(node.id, formData);
    onClose();
  };

  // Render trigger-specific settings
  const renderTriggerSettings = () => (
    <>
      <div className="mb-3">
        <label className="form-label">Bot Trigger</label>
        <div className="btn-group w-100">
          {["newMessage", "matchKeyword"].map((type) => (
            <button
              key={type}
              type="button"
              className={`btn ${
                formData.triggerType === type
                  ? "btn-primary"
                  : "btn-outline-primary"
              }`}
              onClick={() => setFormData({ ...formData, triggerType: type })}
            >
              {type === "newMessage" ? "New Message" : "Match Keyword"}
            </button>
          ))}
        </div>
      </div>

      {formData.triggerType === "matchKeyword" && (
        <>
          <div className="mb-2">
            <label className="form-label fw-bold">Match Conditions</label>
          </div>
          {formData.conditions.length === 0 && (
            <div className="alert alert-info mb-3">
              No conditions added yet. Add conditions to match specific
              keywords.
            </div>
          )}
          {formData.conditions.map((cond) => (
            <div key={cond.id} className="row align-items-center mb-2 g-1">
              <div className="col-12 col-md-3">
                <select
                  className="form-select"
                  value={cond.field}
                  onChange={(e) =>
                    handleConditionChange(cond.id, "field", e.target.value)
                  }
                >
                  <option value="incomingMessage">Incoming Message</option>
                  {/* Add more fields as needed */}
                </select>
              </div>
              <div className="col-12 col-md-3">
                <select
                  className="form-select"
                  value={cond.operator}
                  onChange={(e) =>
                    handleConditionChange(cond.id, "operator", e.target.value)
                  }
                >
                  <option value="is">is</option>
                  <option value="is_not">is not</option>
                  <option value="contains">contains</option>
                  <option value="not_contains">doesn't contain</option>
                  <option value="starts_with">starts with</option>
                  <option value="ends_with">ends with</option>
                </select>
              </div>
              <div className="col-12 col-md-4">
                <input
                  className="form-control"
                  type="text"
                  value={cond.value}
                  placeholder="Enter value"
                  onChange={(e) =>
                    handleConditionChange(cond.id, "value", e.target.value)
                  }
                />
              </div>
              <div className="col-12 col-md-2 text-end">
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeCondition(cond.id)}
                  aria-label="Remove condition"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              onClick={() => addCondition("AND")}
            >
              Add AND Condition
            </button>
            <button
              type="button"
              className="btn btn-outline-warning btn-sm"
              onClick={() => addCondition("OR")}
            >
              Add OR Condition
            </button>
          </div>
        </>
      )}
    </>
  );

  // Render node type specific fields
  const renderFormFields = () => {
    switch (node.data.nodeType) {
      case "start":
        return renderTriggerSettings();
      case "send-a-message":
        return (
          <>
            <div className="mb-3">
              <label className="form-label">Message Content</label>
              <textarea
                className="form-control"
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows="4"
                placeholder="Enter the message to send"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Quick Replies</label>
              <div className="input-group mb-2">
                <input
                  type="text"
                  className="form-control"
                  value={currentQuickReply}
                  onChange={(e) => setCurrentQuickReply(e.target.value)}
                  placeholder="Add quick reply option"
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={addQuickReply}
                >
                  Add
                </button>
              </div>
              {formData.quickReplies.length > 0 && (
                <div className="d-flex flex-wrap gap-2">
                  {formData.quickReplies.map((reply, index) => (
                    <div
                      key={index}
                      className="badge bg-primary d-flex align-items-center"
                    >
                      {reply}
                      <button
                        type="button"
                        className="btn-close btn-close-white ms-2"
                        style={{ fontSize: "0.5rem" }}
                        onClick={() => removeQuickReply(index)}
                        aria-label="Remove"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        );
      case "send-a-button":
        return (
          <>
            <div className="mb-3">
              <label className="form-label">Button Text</label>
              <input
                type="text"
                className="form-control"
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Enter button text"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Button Options</label>
              <input
                type="text"
                className="form-control"
                name="options"
                value={formData.options}
                onChange={handleChange}
                placeholder="Comma separated options (e.g., Yes,No,Maybe)"
              />
              <small className="text-muted">Separate options with commas</small>
            </div>
          </>
        );
      case "send-media":
        return (
          <>
            <div className="mb-3">
              <label className="form-label">Media URL</label>
              <input
                type="url"
                className="form-control"
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="https://example.com/image.jpg"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Caption</label>
              <textarea
                className="form-control"
                name="caption"
                value={formData.caption}
                onChange={handleChange}
                rows="2"
                placeholder="Optional caption for the media"
              />
            </div>
          </>
        );
      case "delay":
        return (
          <div className="mb-3">
            <div className="row">
              <div className="col-6">
                <label className="form-label">Delay Value</label>
                <input
                  type="number"
                  className="form-control"
                  name="delayValue"
                  value={formData.delayValue}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="col-6">
                <label className="form-label">Time Unit</label>
                <select
                  className="form-select"
                  name="delayUnit"
                  value={formData.delayUnit}
                  onChange={handleChange}
                >
                  <option value="seconds">Seconds</option>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          </div>
        );
      case "trigger-webhook":
        return (
          <>
            <div className="mb-3">
              <label className="form-label">Webhook URL</label>
              <input
                type="url"
                className="form-control"
                name="webhookUrl"
                value={formData.webhookUrl}
                onChange={handleChange}
                placeholder="https://api.example.com/endpoint"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">HTTP Method</label>
              <select
                className="form-select"
                name="webhookMethod"
                value={formData.webhookMethod}
                onChange={handleChange}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            {/* Query Parameters Section */}
            <div className="mb-3">
              <label className="form-label">Query Parameters</label>
              <div className="input-group mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Parameter key"
                  value={currentWebhookParam.key}
                  onChange={(e) =>
                    setCurrentWebhookParam({
                      ...currentWebhookParam,
                      key: e.target.value,
                    })
                  }
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Parameter value (can use variables like {{message}})"
                  value={currentWebhookParam.value}
                  onChange={(e) =>
                    setCurrentWebhookParam({
                      ...currentWebhookParam,
                      value: e.target.value,
                    })
                  }
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={addWebhookParam}
                >
                  Add
                </button>
              </div>
              {formData.webhookParams.length > 0 && (
                <div className="border rounded p-2">
                  {formData.webhookParams.map((param) => (
                    <div
                      key={param.id}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <div>
                        <span className="fw-bold">{param.key}:</span>{" "}
                        {param.value}
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => removeWebhookParam(param.id)}
                        aria-label="Remove parameter"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Payload Configuration */}
            <div className="mb-3">
              <label className="form-label">Payload Type</label>
              <select
                className="form-select mb-2"
                name="payloadType"
                value={formData.payloadType}
                onChange={handleChange}
              >
                <option value="static">Static JSON</option>
                <option value="dynamic">Dynamic Template</option>
              </select>

              {formData.payloadType === "static" ? (
                <textarea
                  className="form-control"
                  name="payload"
                  value={formData.payload || "{}"}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Enter JSON payload"
                />
              ) : (
                <div>
                  <textarea
                    className="form-control"
                    name="payloadTemplate"
                    value={formData.payloadTemplate}
                    onChange={handleChange}
                    rows="4"
                    placeholder={`Enter template with variables like:
{
  "message": "{{message}}",
  "user": "{{user.name}}"
}`}
                  />
                  <small className="text-muted">
                    {`Use double curly braces for variables (e.g., {{message}}, {{user.id}})`}
                  </small>
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">Custom Headers</label>
              <div className="input-group mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Header key"
                  value={currentWebhookHeader.key}
                  onChange={(e) =>
                    setCurrentWebhookHeader({
                      ...currentWebhookHeader,
                      key: e.target.value,
                    })
                  }
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Header value"
                  value={currentWebhookHeader.value}
                  onChange={(e) =>
                    setCurrentWebhookHeader({
                      ...currentWebhookHeader,
                      value: e.target.value,
                    })
                  }
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={addWebhookHeader}
                >
                  Add
                </button>
              </div>
              {formData.webhookHeaders.length > 0 && (
                <div className="border rounded p-2">
                  {formData.webhookHeaders.map((header) => (
                    <div
                      key={header.id}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <div>
                        <span className="fw-bold">{header.key}:</span>{" "}
                        {header.value}
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => removeWebhookHeader(header.id)}
                        aria-label="Remove header"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        );
      default:
        return (
          <div className="mb-3">
            <label className="form-label">Content</label>
            <input
              type="text"
              className="form-control"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder="Enter node content"
            />
          </div>
        );
    }
  };

  return (
    <div
      className="node-edit-panel p-3 border bg-light rounded"
      style={{ minWidth: "350px" }}
    >
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Edit {node.data.nodeType || "Node"}</h5>
        <button
          className="btn-close"
          onClick={onClose}
          aria-label="Close panel"
        ></button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Node Label</label>
          <input
            type="text"
            className="form-control"
            name="label"
            value={formData.label}
            onChange={handleChange}
            required
            placeholder="Enter a descriptive label"
          />
        </div>

        {renderFormFields()}

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              if (
                window.confirm("Are you sure you want to delete this node?")
              ) {
                onDelete(node.id);
                onClose();
              }
            }}
          >
            <i className="bi bi-trash me-2"></i>Delete
          </button>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Changes
            </button>
          </div>
        </div>
      </form>

      {/* Add this inside the main panel, before the form closing tag */}
      <div className="mt-4">
        <h6>Outgoing Connections</h6>
        {edges
          .filter((e) => e.source === node.id)
          .map((edge) => {
            const targetNode = nodes.find((n) => n.id === edge.target);
            return (
              <div
                key={edge.id}
                className="d-flex justify-content-between align-items-center mb-2"
              >
                <span>→ {targetNode?.data?.label || "Untitled Node"}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handleEditEdge(edge)}
                >
                  Edit Conditions
                </button>
              </div>
            );
          })}
      </div>

      {/* Add this right before the final closing </div> */}
      {isEditingEdge && (
        <EdgeConditionEditor
          edge={currentEdge}
          onSave={handleSaveEdgeConditions}
          onClose={() => setIsEditingEdge(false)}
        />
      )}
    </div>
  );
};

const EdgeConditionEditor = ({ edge, onSave, onClose }) => {
  const [conditions, setConditions] = useState(edge?.conditions || []);

  const handleConditionChange = (index, field, value) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [field]: value };
    setConditions(newConditions);
  };

  const addCondition = (type = "AND") => {
    setConditions([
      ...conditions,
      {
        id: Date.now(),
        field: "incomingMessage",
        operator: "is",
        value: "",
        type,
      },
    ]);
  };

  const removeCondition = (id) => {
    setConditions(conditions.filter((c) => c.id !== id));
  };

  return (
    <div className="edge-condition-editor p-3 border rounded bg-white">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Edge Conditions</h5>
        <button className="btn-close" onClick={onClose}></button>
      </div>

      {conditions.length === 0 && (
        <div className="alert alert-info mb-3">
          No conditions set - this path will always be followed if no other
          conditions match
        </div>
      )}

      {conditions.map((cond) => (
        <div key={cond.id} className="row align-items-center mb-2 g-1">
          <div className="col-12 col-md-3">
            <select
              className="form-select"
              value={cond.field}
              onChange={(e) =>
                handleConditionChange(
                  conditions.findIndex((c) => c.id === cond.id),
                  "field",
                  e.target.value
                )
              }
            >
              <option value="incomingMessage">Message Content</option>
              {/* Add more fields as needed */}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <select
              className="form-select"
              value={cond.operator}
              onChange={(e) =>
                handleConditionChange(
                  conditions.findIndex((c) => c.id === cond.id),
                  "operator",
                  e.target.value
                )
              }
            >
              <option value="is">is</option>
              <option value="is_not">is not</option>
              <option value="contains">contains</option>
              <option value="not_contains">doesn't contain</option>
              <option value="starts_with">starts with</option>
              <option value="ends_with">ends with</option>
            </select>
          </div>
          <div className="col-12 col-md-4">
            <input
              className="form-control"
              type="text"
              value={cond.value}
              placeholder="Enter value"
              onChange={(e) =>
                handleConditionChange(
                  conditions.findIndex((c) => c.id === cond.id),
                  "value",
                  e.target.value
                )
              }
            />
          </div>
          <div className="col-12 col-md-2 text-end">
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => removeCondition(cond.id)}
            >
              ✕
            </button>
          </div>
        </div>
      ))}

      <div className="d-flex gap-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-success btn-sm"
          onClick={() => addCondition("AND")}
        >
          Add AND Condition
        </button>
        <button
          type="button"
          className="btn btn-outline-warning btn-sm"
          onClick={() => addCondition("OR")}
        >
          Add OR Condition
        </button>
      </div>

      <div className="d-flex justify-content-end">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onSave(conditions)}
        >
          Save Conditions
        </button>
      </div>
    </div>
  );
};

export default NodeEditPanel;
